/**
 * Phase 5 ILLUSTRATIVE coverage sample data. PaCC does NOT yet ingest media
 * coverage — this file demonstrates the visual logic only. Every record
 * carries the same honesty states the real pipeline will:
 *
 *   - per-window coverage volume (silent / low / medium / high + count)
 *   - source-type breakdown (agency / news / social)
 *   - sentiment distribution (observation about tone, never a judgement)
 *   - asymmetry flag (under-reported / high-profile) when present
 *   - the noData state for an outbreak with NO ingested coverage — surfaced
 *     as "No coverage data ingested" (NOT silent — those are opposite findings)
 *
 * NEVER use this data for operational decisions. The EDU banner says so;
 * this comment doubles down.
 *
 * Wire to the real pipeline when ProMED / WHO situation reports / GDELT
 * integrations land (Phase 5 data sub-track — see the brief).
 */

import {
  type OutbreakCoverage, type CoverageArticle, type CoverageLevel,
  computeAsymmetry,
} from './types';

/** The threat-tier→expected-coverage mapping. Currently 1:1 ordinal:
 *  CRITICAL/HIGH outbreaks expect high coverage; ELEVATED expects medium;
 *  WATCH expects low; ROUTINE expects silent. This is the pure-data signal
 *  the asymmetry function compares against. */
const EXPECTED_COVERAGE_BY_TIER: Record<number, CoverageLevel> = {
  1: 3, // CRITICAL
  2: 2, // HIGH (note: spec used 2 here, not 3 — high-coverage threshold)
  3: 2, // ELEVATED
  4: 1, // WATCH
  0: 0, // ROUTINE
};

interface SampleOutbreak {
  id: string;
  common: string;
  family: 'respiratory' | 'haemorrhagic' | 'vector' | 'enteric' | 'zoonotic';
  admin: string;
  iso3: string;
  threatTier: 0 | 1 | 2 | 3 | 4;
  windows: OutbreakCoverage['windows'];
  byType: OutbreakCoverage['byType'];
  sentiment: OutbreakCoverage['sentiment'];
  highConfidenceCount: number;
  ambiguousCount: number;
  noData?: boolean;
}

/** The 9 demo outbreaks from the CD spec (focal = Ebola Zaire DRC). */
const SAMPLE_OUTBREAKS: SampleOutbreak[] = [
  {
    id: 'ob-ebola-drc', common: 'Ebola (Zaïre)', family: 'haemorrhagic',
    admin: 'North Kivu, DR Congo', iso3: 'COD', threatTier: 1,
    windows: { d7: { level: 1, count: 9 }, d30: { level: 2, count: 41 }, onset: { level: 2, count: 63 } },
    byType: { agency: 6, news: 3, social: 0 },
    sentiment: { measured: 6, neutral: 2, alarmist: 1 },
    highConfidenceCount: 6, ambiguousCount: 3,
  },
  {
    id: 'ob-marburg-rw', common: 'Marburg', family: 'haemorrhagic',
    admin: 'Kigali, Rwanda', iso3: 'RWA', threatTier: 1,
    windows: { d7: { level: 0, count: 2 }, d30: { level: 1, count: 11 }, onset: { level: 1, count: 14 } },
    byType: { agency: 2, news: 0, social: 0 },
    sentiment: { measured: 2, neutral: 0, alarmist: 0 },
    highConfidenceCount: 2, ambiguousCount: 0,
  },
  {
    id: 'ob-nipah-bd', common: 'Nipah', family: 'zoonotic',
    admin: 'Rajshahi, Bangladesh', iso3: 'BGD', threatTier: 1,
    windows: { d7: { level: 0, count: 1 }, d30: { level: 0, count: 3 }, onset: { level: 1, count: 6 } },
    byType: { agency: 1, news: 0, social: 0 },
    sentiment: { measured: 1, neutral: 0, alarmist: 0 },
    highConfidenceCount: 1, ambiguousCount: 0,
  },
  {
    id: 'ob-h5n1-us', common: 'Influenza A(H5N1)', family: 'respiratory',
    admin: 'California, USA', iso3: 'USA', threatTier: 2,
    windows: { d7: { level: 3, count: 240 }, d30: { level: 3, count: 1120 }, onset: { level: 3, count: 1600 } },
    byType: { agency: 22, news: 180, social: 38 },
    sentiment: { measured: 90, neutral: 70, alarmist: 80 },
    highConfidenceCount: 210, ambiguousCount: 30,
  },
  {
    id: 'ob-measles-uk', common: 'Measles', family: 'respiratory',
    admin: 'Birmingham, England', iso3: 'GBR', threatTier: 3,
    windows: { d7: { level: 2, count: 34 }, d30: { level: 2, count: 88 }, onset: { level: 2, count: 120 } },
    byType: { agency: 4, news: 26, social: 4 },
    sentiment: { measured: 18, neutral: 12, alarmist: 4 },
    highConfidenceCount: 30, ambiguousCount: 4,
  },
  {
    id: 'ob-cholera-sd', common: 'Cholera', family: 'enteric',
    admin: 'Khartoum, Sudan', iso3: 'SDN', threatTier: 2,
    windows: { d7: { level: 0, count: 2 }, d30: { level: 1, count: 12 }, onset: { level: 1, count: 19 } },
    byType: { agency: 2, news: 0, social: 0 },
    sentiment: { measured: 2, neutral: 0, alarmist: 0 },
    highConfidenceCount: 2, ambiguousCount: 0,
  },
  {
    id: 'ob-mpox-cd', common: 'Mpox (clade I)', family: 'zoonotic',
    admin: 'Kinshasa, DR Congo', iso3: 'COD', threatTier: 3,
    windows: { d7: { level: 2, count: 28 }, d30: { level: 3, count: 210 }, onset: { level: 3, count: 389 } },
    byType: { agency: 8, news: 16, social: 4 },
    sentiment: { measured: 14, neutral: 10, alarmist: 4 },
    highConfidenceCount: 24, ambiguousCount: 4,
  },
  {
    id: 'ob-dengue-br', common: 'Dengue', family: 'vector',
    admin: 'Brasília, Brazil', iso3: 'BRA', threatTier: 4,
    windows: { d7: { level: 1, count: 6 }, d30: { level: 1, count: 18 }, onset: { level: 2, count: 54 } },
    byType: { agency: 1, news: 5, social: 0 },
    sentiment: { measured: 4, neutral: 2, alarmist: 0 },
    highConfidenceCount: 6, ambiguousCount: 0,
  },
  {
    id: 'ob-lassa-ng', common: 'Lassa fever', family: 'haemorrhagic',
    admin: 'Plateau, Nigeria', iso3: 'NGA', threatTier: 2, noData: true,
    windows: { d7: { level: 0, count: 0 }, d30: { level: 0, count: 0 }, onset: { level: 0, count: 0 } },
    byType: { agency: 0, news: 0, social: 0 },
    sentiment: { measured: 0, neutral: 0, alarmist: 0 },
    highConfidenceCount: 0, ambiguousCount: 0,
  },
];

/** Build the OutbreakCoverage records — asymmetry derived from the d7 window. */
export const SAMPLE_COVERAGE: Array<OutbreakCoverage & {
  common: string; family: SampleOutbreak['family']; admin: string; iso3: string;
  threatTier: SampleOutbreak['threatTier'];
}> = SAMPLE_OUTBREAKS.map(o => ({
  outbreakId: o.id,
  common: o.common,
  family: o.family,
  admin: o.admin,
  iso3: o.iso3,
  threatTier: o.threatTier,
  windows: o.windows,
  byType: o.byType,
  sentiment: o.sentiment,
  highConfidenceCount: o.highConfidenceCount,
  ambiguousCount: o.ambiguousCount,
  asymmetry: o.noData ? null : computeAsymmetry(
    o.windows.d7.level,
    EXPECTED_COVERAGE_BY_TIER[o.threatTier] ?? 0,
  ),
  ...(o.noData ? { noData: true } : {}),
}));

/** Sample articles for the focal outbreak (Ebola DRC). 9 items spanning all
 *  three credibility tiers + all three sentiment classes + the high vs
 *  ambiguous mapping-confidence split.
 *
 *  The `url`s are illustrative-but-realistic so the Sources list can exercise
 *  the media-link safety path (`safeArticleHref` + `<ArticleLink>`):
 *   - T1/T2 rows carry well-formed https URLs (rendered clean + clickable).
 *   - One row (a8) carries a POISONED `javascript:` scheme to prove the guard
 *     downgrades it to non-clickable text rather than emitting a live link.
 *     This is the synthetic stand-in for a bad-actor-planted GDELT result.
 *   - One row (a9) has NO url at all (the guard renders it as plain text).
 *  None of these are operational — see the file header. */
export const SAMPLE_ARTICLES: CoverageArticle[] = [
  { id: 'a1', outbreakId: 'ob-ebola-drc', headline: 'Ministry confirms additional Ebola cases in eastern province; response teams deployed', source: 'who.int', type: 'official', cred: 1, date: daysAgo(2), sentiment: 'measured', conf: 'high', url: 'https://www.who.int/emergencies/disease-outbreak-news' },
  { id: 'a2', outbreakId: 'ob-ebola-drc', headline: 'ProMED post: cluster of viral haemorrhagic fever, North Kivu — request for information', source: 'promedmail.org', type: 'official', cred: 1, date: daysAgo(3), sentiment: 'measured', conf: 'high', url: 'https://promedmail.org/promed-post/' },
  { id: 'a3', outbreakId: 'ob-ebola-drc', headline: 'Regional health authority issues situation update on haemorrhagic fever response', source: 'africacdc.org', type: 'official', cred: 1, date: daysAgo(4), sentiment: 'measured', conf: 'high', url: 'https://africacdc.org/news-item/' },
  { id: 'a4', outbreakId: 'ob-ebola-drc', headline: 'Health workers race to contain outbreak in conflict-affected region', source: 'reuters.com', type: 'mainstream', cred: 2, date: daysAgo(2), sentiment: 'neutral', conf: 'high', url: 'https://www.reuters.com/world/africa/' },
  { id: 'a5', outbreakId: 'ob-ebola-drc', headline: 'What to know about the latest Ebola flare-up', source: 'apnews.com', type: 'mainstream', cred: 2, date: daysAgo(3), sentiment: 'neutral', conf: 'high', url: 'https://apnews.com/hub/ebola' },
  { id: 'a6', outbreakId: 'ob-ebola-drc', headline: 'Deadly virus spreads as fears grow in border towns', source: 'unknown-aggregator.example', type: 'aggregator', cred: 3, date: daysAgo(1), sentiment: 'alarmist', conf: 'high', url: 'https://unknown-aggregator.example/article/123' },
  { id: 'a7', outbreakId: 'ob-ebola-drc', headline: 'Outbreak said to be worsening, sources claim', source: 'syndicated-wire.example', type: 'aggregator', cred: 3, date: daysAgo(2), sentiment: 'alarmist', conf: 'ambiguous', url: 'http://syndicated-wire.example/pickup/9912' },
  // Adversarial sample: a poisoned scheme as if a bad actor planted it in a
  // GDELT-indexed page. safeArticleHref MUST reject this → non-clickable text.
  { id: 'a8', outbreakId: 'ob-ebola-drc', headline: 'Fever outbreak reported in central Africa (location unconfirmed)', source: 'gdelt-cluster.example', type: 'aggregator', cred: 3, date: daysAgo(5), sentiment: 'neutral', conf: 'ambiguous', url: 'javascript:alert(document.cookie)' },
  // No URL at all — rendered as plain text (the omitted-url state).
  { id: 'a9', outbreakId: 'ob-ebola-drc', headline: 'Aid agency briefing references multiple concurrent health emergencies', source: 'gdelt-cluster.example', type: 'aggregator', cred: 3, date: daysAgo(6), sentiment: 'neutral', conf: 'ambiguous' },
];

function daysAgo(d: number): string {
  return new Date(Date.now() - d * 86_400_000).toISOString();
}
